import { z } from 'zod';

export const ACTIVITY_MEDIA_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'video/mp4',
  'video/webm',
  // iPhone records to .mov (HEVC) by default unless "Most Compatible" is
  // enabled in Settings > Camera > Formats. The media-optimization worker
  // re-encodes to H.264 mp4 on confirm, so accepting quicktime is safe.
  'video/quicktime',
  // Android cameras (Samsung default, low-end devices) emit 3gpp.
  'video/3gpp',
] as const;

export type ActivityMediaMimeType = (typeof ACTIVITY_MEDIA_MIME_TYPES)[number];

/**
 * Video-only subset of ACTIVITY_MEDIA_MIME_TYPES — used in mini-app responses
 * that only carry videos (images travel a different path). Must stay in sync
 * with the video entries above; the assertion below catches drift at compile
 * time so a new video MIME added to the parent list also lands here.
 */
export const ACTIVITY_MEDIA_VIDEO_MIME_TYPES = [
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/3gpp',
] as const;
export type ActivityMediaVideoMimeType = (typeof ACTIVITY_MEDIA_VIDEO_MIME_TYPES)[number];

// Compile-time check: every entry of the video subset must be a member of the
// parent ACTIVITY_MEDIA_MIME_TYPES list. If a video MIME is added to the
// parent list but not here (or vice versa), this assignment fails typecheck.
const _videoSubsetIsSubset: ActivityMediaMimeType =
  null as unknown as ActivityMediaVideoMimeType;
void _videoSubsetIsSubset;

/**
 * Lifecycle of the media-optimization worker writing to detalle_actividad_fotos.
 * - PENDING: enqueued by confirm step, not yet picked up by the BullMQ worker.
 * - PROCESSING: worker started, ffmpeg transcoding in progress; optimizedS3Key
 *   is still null at this point.
 * - COMPLETED: transcoding finished, optimizedS3Key + videoThumbnailS3Key
 *   populated. Only state safe to render `<video src=optimized>`.
 * - FAILED: worker errored (ffmpeg crash, S3 transient, OOM). processingError
 *   carries the message. Caller must NOT play raw HEVC fallback on Chrome
 *   Android — show an error UI instead.
 */
export const ACTIVITY_MEDIA_PROCESSING_STATUS = ['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED'] as const;
export type ActivityMediaProcessingStatus = (typeof ACTIVITY_MEDIA_PROCESSING_STATUS)[number];

/** Max 10 MB for images, 50 MB for videos. */
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const MAX_VIDEO_SIZE = 50 * 1024 * 1024;

export const ActivityMediaPresignSchema = z.object({
  fileName: z.string().min(1).max(255),
  mimeType: z.enum(ACTIVITY_MEDIA_MIME_TYPES),
  fileSize: z.number().int().positive(),
}).refine((data) => {
  const maxSize = data.mimeType.startsWith('video/') ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
  return data.fileSize <= maxSize;
}, { message: 'Archivo excede el tamano maximo permitido', path: ['fileSize'] });

export const ActivityMediaConfirmSchema = z.object({
  s3Key: z.string().min(1),
  thumbnailS3Key: z.string().optional(),
  fileName: z.string().min(1).max(255),
  fileSize: z.number().int().positive(),
  mimeType: z.enum(ACTIVITY_MEDIA_MIME_TYPES),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  descripcion: z.string().max(500).optional(),
});

export type ActivityMediaPresign = z.infer<typeof ActivityMediaPresignSchema>;
export type ActivityMediaConfirm = z.infer<typeof ActivityMediaConfirmSchema>;

export const ACTIVITY_MEDIA_CONFIG = {
  maxImageSize: MAX_IMAGE_SIZE,
  maxVideoSize: MAX_VIDEO_SIZE,
  maxMediaPerActivity: 10,
  presignedUrlExpiry: 3600,
} as const;
